import * as React from 'react';
import { Controller, type Control, useWatch } from 'react-hook-form';
import { CriteriaRuleBuilder } from '@packages/ui/patterns/CriteriaRuleBuilder';
import type {
  CriteriaRoot,
  FieldCatalogEntry,
  ResolveOptionsContext,
  RuleBuilderOption,
} from '@packages/ui/patterns/CriteriaRuleBuilder';
import { normalizeCriteriaRoot } from '@packages/ui/patterns/CriteriaRuleBuilder/criteriaTreeMutators';
import { loadRuleBuilderConfig } from '@packages/domain/shared/rule-builder/loadConfig';
import {
  buildRuleBuilderActionParams,
  getRuleBuilderOptionFetchPlan,
  mapRowsToRuleBuilderOptions,
  resolveCatalogActionId,
} from '@packages/domain/shared/rule-builder/optionResolution';
import { ActionRegistry } from '@packages/table-builder/registry';
import { FormField } from '@packages/ui/patterns/FormField';
import { useFormEngineForm, useFormEngineState } from '../context/FormEngineContext';

export interface FormRuleBuilderFieldRendererProps {
  name: string;
  label: string;
  control: Control<Record<string, unknown>>;
  configModule: string;
  readOnly?: boolean;
  required?: boolean;
  /** Overrides config.fieldCatalog.actionId when set in domain adapterProps */
  catalogActionId?: string;
}

function needsCriteriaSeed(value: unknown): boolean {
  const root = value as CriteriaRoot | null | undefined;
  return root == null || !Array.isArray(root.Conditions) || root.Conditions.length === 0;
}

export function FormRuleBuilderFieldRenderer({
  name,
  label,
  control,
  configModule,
  readOnly = false,
  required,
  catalogActionId: catalogActionIdOverride,
}: FormRuleBuilderFieldRendererProps) {
  const config = React.useMemo(() => loadRuleBuilderConfig(configModule), [configModule]);
  const { form } = useFormEngineForm();
  const { params: blockParams } = useFormEngineState();
  const [fieldCatalog, setFieldCatalog] = React.useState<FieldCatalogEntry[]>([]);

  const catalogActionId = React.useMemo(
    () => resolveCatalogActionId(config, catalogActionIdOverride),
    [config, catalogActionIdOverride],
  );

  const contextFieldNames = config.actionContext?.formFields ?? [];
  const watchedContextValues = useWatch({
    control,
    name: contextFieldNames.length > 0 ? contextFieldNames : undefined,
    disabled: contextFieldNames.length === 0,
  });

  const watchedCriteria = useWatch({ control, name });
  const watchedFormValues = useWatch({ control }) as Record<string, unknown> | undefined;

  React.useEffect(() => {
    if (readOnly || !needsCriteriaSeed(watchedCriteria)) return;
    const seeded = normalizeCriteriaRoot(
      null,
      config.defaultRootOperator,
      config.defaultGroupOperator,
    );
    form.setValue(name, seeded, { shouldDirty: false, shouldValidate: false });
  }, [readOnly, watchedCriteria, config, form, name]);

  const buildActionParams = React.useCallback(
    (formValues: Record<string, unknown>) =>
      buildRuleBuilderActionParams(config, formValues, blockParams as Record<string, unknown>),
    [config, blockParams],
  );

  React.useEffect(() => {
    if (!catalogActionId) {
      if (config.fieldCatalog?.source === 'inline' && config.fieldCatalog.entries) {
        setFieldCatalog(config.fieldCatalog.entries);
      }
      return;
    }

    let cancelled = false;
    const formValues = form.getValues() as Record<string, unknown>;
    void ActionRegistry.executeData(catalogActionId, buildActionParams(formValues)).then(
      (result) => {
        const rows = result as unknown;
        if (!cancelled && Array.isArray(rows)) {
          setFieldCatalog(rows as FieldCatalogEntry[]);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [catalogActionId, config.fieldCatalog, form, buildActionParams, watchedContextValues]);

  const resolveOptions = React.useCallback(
    async (entry: FieldCatalogEntry, context: ResolveOptionsContext): Promise<RuleBuilderOption[]> => {
      if (entry.staticOptions?.length) {
        return entry.staticOptions;
      }

      const plan = getRuleBuilderOptionFetchPlan(config, entry);
      if (!plan) return [];

      const actionParams = buildRuleBuilderActionParams(
        config,
        context.formValues,
        blockParams as Record<string, unknown>,
      );

      for (const requiredKey of plan.requires) {
        if (!actionParams[requiredKey]) return [];
      }

      const rows = await ActionRegistry.executeData(plan.actionId, actionParams);
      return mapRowsToRuleBuilderOptions(rows, plan);
    },
    [config, blockParams],
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const formValues =
          watchedFormValues ?? (form.getValues() as Record<string, unknown>);
        const normalized = normalizeCriteriaRoot(
          field.value as CriteriaRoot | null,
          config.defaultRootOperator,
          config.defaultGroupOperator,
        );

        return (
          <FormField
            label={label?.trim() ? label : undefined}
            htmlFor={name}
            required={required}
          >
            <CriteriaRuleBuilder
              semanticId={`${name}RuleBuilder`}
              config={config}
              fieldCatalog={fieldCatalog}
              value={normalized}
              onChange={(next) => field.onChange(next)}
              readOnly={readOnly}
              title=''
              formValues={formValues}
              resolveOptions={resolveOptions}
            />
          </FormField>
        );
      }}
    />
  );
}

FormRuleBuilderFieldRenderer.displayName = 'FormRuleBuilderFieldRenderer';
